// frontend/modules/document-type/components/DocumentTypeCard.tsx
import React from 'react';
import { Card, Tag, Space, Button, Typography, Tooltip, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import {DocumentType} from "@/modules/document-type/types/document-type.types";

const { Text, Paragraph } = Typography;

interface DocumentTypeCardProps {
    documentType: DocumentType;
    onEdit?: (code: string) => void;
    onDelete?: (code: string) => void;
    onView?: (code: string) => void;
    loading?: boolean;
}

export const DocumentTypeCard: React.FC<DocumentTypeCardProps> = ({
    documentType,
    onEdit,
    onDelete,
    onView,
    loading = false
}) => {
    const formatFileSize = (sizeKb?: number) => {
        if (!sizeKb) return null;
        if (sizeKb >= 1024) {
            return `${(sizeKb / 1024).toFixed(1)} MB`;
        }
        return `${sizeKb} KB`;
    };

    const renderFileTypes = () => {
        if (!documentType.allowedFileTypes || documentType.allowedFileTypes.length === 0) {
            return <Text type="secondary">All file types allowed</Text>;
        }

        const displayTypes = documentType.allowedFileTypes.slice(0, 3);
        const remainingCount = documentType.allowedFileTypes.length - 3;

        return (
            <Space wrap>
                {displayTypes.map(type => (
                    <Tag key={type}>
                        .{type}
                    </Tag>
                ))}
                {remainingCount > 0 && (
                    <Tooltip title={documentType.allowedFileTypes.slice(3).map(t => `.${t}`).join(', ')}>
                        <Tag>+{remainingCount} more</Tag>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const renderSizeConstraints = () => {
        const { minUploadSizeKb, maxUploadSizeKb } = documentType;
        
        if (!minUploadSizeKb && !maxUploadSizeKb) {
            return <Text type="secondary">No size restrictions</Text>;
        }

        const constraints = [];
        if (minUploadSizeKb) {
            constraints.push(`Min: ${formatFileSize(minUploadSizeKb)}`);
        }
        if (maxUploadSizeKb) {
            constraints.push(`Max: ${formatFileSize(maxUploadSizeKb)}`);
        }

        return <Text type="secondary">{constraints.join(', ')}</Text>;
    };

    return (
        <Card
            size="small"
            title={
                <Space>
                    <FileTextOutlined />
                    <Text strong>{documentType.name}</Text>
                    <Text type="secondary" code style={{ fontSize: 12 }}>
                        {documentType.code}
                    </Text>
                </Space>
            }
            extra={
                <Space>
                    {documentType.isActive ? (
                        <Tag color="green">Active</Tag>
                    ) : (
                        <Tag color="red">Inactive</Tag>
                    )}
                    {documentType.isRequired && (
                        <Tag color="orange">Required</Tag>
                    )}
                </Space>
            }
            actions={[
                <Tooltip title="View Details" key="view">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => onView?.(documentType.code)}
                        disabled={loading}
                    />
                </Tooltip>,
                <Tooltip title="Edit" key="edit">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => onEdit?.(documentType.code)}
                        disabled={loading}
                    />
                </Tooltip>,
                <Tooltip title="Delete" key="delete">
                    <Popconfirm
                        title="Delete Document Type"
                        description={`Are you sure you want to delete "${documentType.name}"?`}
                        onConfirm={() => onDelete?.(documentType.code)}
                        okText="Yes"
                        cancelText="No"
                        disabled={loading}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={loading}
                        />
                    </Popconfirm>
                </Tooltip>
            ]}
            style={{ marginBottom: 16 }}
        >
            {documentType.description && (
                <Paragraph
                    ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
                    style={{ marginBottom: 12 }}
                >
                    {documentType.description}
                </Paragraph>
            )}

            <div style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 12 }}>File Types: </Text>
                {renderFileTypes()}
            </div>

            <div style={{ marginBottom: 8 }}>
                <Text strong style={{ fontSize: 12 }}>Size Limits: </Text>
                {renderSizeConstraints()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Order: {documentType.displayOrder}
                </Text>
                {documentType.createdBy && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Created by: {documentType.createdBy}
                    </Text>
                )}
            </div>
        </Card>
    );
};
